import {useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import type {MyDealerService2_1} from '@volkswagen-onehub/mydealer-service'
import {RootState} from './rootReducer'
import store from './store'
import {
    AppDispatch,
    fetchAllDealers,
    fetchAllPromotions,
    fetchAllProvinces,
    fetchConfigurationPromo,
    fetchMyDealerBranches,
    fetchMyDealerPromotions,
} from './thunk'
import {
    changePreselectedDealer,
    setMyDealerBranches,
    setMyDealerCode,
} from '../features/dealers/dealerSlice'
import {setDealerWeb, setLoading} from '../features/general/generalSlice'
import {setUrlParameters} from '../features/configurationPromo/configurationPromoSlice'
import {getAllQueryParams} from '../shared/utilities'
import {Global} from '../shared/variables'

interface PromotionContext {
    backendUrl: string
    campaignCode: string
    brand: string
    layout: string
}

type MyDealerService = Pick<
    MyDealerService2_1,
    'getSelectedDealer' | 'subscribe'
>

export function usePromotionData(
    {backendUrl, campaignCode, brand, layout}: PromotionContext,
    myDealerService?: MyDealerService
): boolean {
    const dispatch = useDispatch() as AppDispatch
    const myDealerCode = useSelector(
        (state: RootState) => state.dealers.myDealerCode
    )
    const searchParams = useSelector(
        (state: RootState) =>
            state.configurationPromo.configuration.dealers_search_params
    )
    const contextKey = JSON.stringify([backendUrl, campaignCode, brand, layout])
    const [loadedContext, setLoadedContext] = useState<string | null>(null)
    const isTeaser = layout === Global.TEASER_LAYOUT
    const loadingConfiguration = !isTeaser && loadedContext !== contextKey

    useEffect(() => {
        setLoadedContext(null)
        if (isTeaser) {
            return
        }

        let active = true
        const requests: {abort: () => void}[] = []
        dispatch(setLoading(true))
        const configurationRequest = dispatch(fetchConfigurationPromo())
        requests.push(configurationRequest)

        configurationRequest
            .unwrap()
            .then((configuration) => {
                if (!active) {
                    return
                }
                requests.push(dispatch(fetchAllProvinces()))
                requests.push(dispatch(fetchAllDealers()))
                const params = getAllQueryParams()
                const allowed = configuration.allowed_parameters || []
                const filtered: Record<string, string> = {}
                Object.keys(params)
                    .filter((key) => allowed.includes(key))
                    .forEach((key) => {
                        filtered[String(key)] = params[String(key)]
                    })
                dispatch(setUrlParameters(filtered))
                setLoadedContext(contextKey)
            })
            .catch(() => {
                if (active) {
                    dispatch(setLoading(false))
                }
            })

        return () => {
            active = false
            requests.forEach((request) => request.abort())
        }
    }, [dispatch, contextKey, isTeaser])

    useEffect(() => {
        if (!myDealerService) {
            return
        }

        const selectDealer = (
            dealer: ReturnType<MyDealerService['getSelectedDealer']>
        ) => {
            const code = dealer?.id || ''
            dispatch(setMyDealerCode(code))
            dispatch(setDealerWeb(Boolean(code)))
        }
        const unsubscribe = myDealerService.subscribe((dealer) => {
            dispatch(changePreselectedDealer(false))
            selectDealer(dealer)
        })
        if (!store.getState().dealers.changePreselectedDealerData) {
            selectDealer(myDealerService.getSelectedDealer())
        }

        return unsubscribe
    }, [dispatch, myDealerService])

    useEffect(() => {
        // A saved MyDealer is available before the campaign response. Never
        // resolve its branches using the initial/default campaign filters.
        if (loadingConfiguration) {
            return
        }

        let active = true
        const requests: {abort: () => void}[] = []
        const loadAllPromotions = () => {
            const request = dispatch(fetchAllPromotions())
            requests.push(request)
            return request.unwrap()
        }

        if (myDealerCode) {
            dispatch(setLoading(true))
            const branches = dispatch(fetchMyDealerBranches(myDealerCode))
            requests.push(branches)
            const promotions = isTeaser
                ? dispatch(fetchMyDealerPromotions(myDealerCode))
                : undefined
            if (promotions) {
                requests.push(promotions)
            }
            // The teaser needs every promotion too: for the "all promotions"
            // CTA and as fallback when the MyDealer is not a network dealer.
            const allPromotions = isTeaser
                ? loadAllPromotions().catch(() => undefined)
                : undefined
            Promise.all([
                branches.unwrap(),
                promotions?.unwrap(),
                allPromotions,
            ])
                .then(([dealers]) => {
                    if (active) {
                        if (!isTeaser) {
                            dispatch(
                                setDealerWeb(
                                    dealers.some(
                                        (dealer) =>
                                            dealer.companyCode === myDealerCode
                                    )
                                )
                            )
                        }
                        dispatch(setLoading(false))
                    }
                })
                .catch(async (error: unknown) => {
                    if (!active) {
                        return
                    }
                    branches.abort()
                    promotions?.abort()
                    console.warn('[app] dealer fetch failed:', error)
                    dispatch(setDealerWeb(false))
                    dispatch(setMyDealerBranches([]))
                    await allPromotions
                    if (active) {
                        dispatch(setLoading(false))
                    }
                })
        } else {
            dispatch(setMyDealerBranches([]))
            if (isTeaser) {
                dispatch(setLoading(true))
                loadAllPromotions()
                    .catch(() => undefined)
                    .finally(() => {
                        if (active) {
                            dispatch(setLoading(false))
                        }
                    })
            } else {
                dispatch(setLoading(false))
            }
        }

        return () => {
            active = false
            requests.forEach((request) => request.abort())
        }
    }, [
        dispatch,
        contextKey,
        loadingConfiguration,
        myDealerCode,
        isTeaser,
        searchParams.brand,
        searchParams.activity_type,
        searchParams.contract_type,
    ])

    return loadingConfiguration
}
